import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { logsApi, groupsApi } from "@/services/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const typeBadge: Record<string, string> = {
  info: "bg-info/20 text-info border-info/30",
  warning: "bg-warning/20 text-warning border-warning/30",
  error: "bg-destructive/20 text-destructive border-destructive/30",
};

export default function Logs() {
  const [groupFilter, setGroupFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState<any | null>(null);

  const { data: groups = [] } = useQuery({ queryKey: ["groups"], queryFn: groupsApi.list });

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["logs", groupFilter, typeFilter],
    queryFn: () => logsApi.list({
      ...(groupFilter !== "all" ? { group_id: groupFilter } : {}),
      ...(typeFilter !== "all" ? { type: typeFilter } : {}),
      limit: 50,
    }),
    refetchInterval: 15000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ScrollText className="w-6 h-6 text-secondary" />
        <h1 className="text-2xl font-bold text-foreground">Logs do Bot</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Grupo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Grupos</SelectItem>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Card><CardContent className="p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Mensagem</TableHead>
                  <TableHead>Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer" onClick={() => setSelectedLog(l)}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString("pt-BR")}
                    </TableCell>
                    <TableCell className="text-sm">{l.group_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={typeBadge[l.type] || ""}>{l.type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-[300px] truncate">{l.message}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{l.details || "-"}</TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nenhum log encontrado</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Detalhes do Log
              {selectedLog && (
                <Badge variant="outline" className={typeBadge[selectedLog.type] || ""}>{selectedLog.type}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedLog && new Date(selectedLog.created_at).toLocaleString("pt-BR")}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Grupo</p>
                <p className="text-sm text-foreground">{selectedLog.group_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Mensagem</p>
                <p className="text-sm text-foreground whitespace-pre-wrap break-words">{selectedLog.message}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Detalhes</p>
                <pre className="text-xs text-foreground bg-muted p-3 rounded-md whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                  {selectedLog.details || "Sem detalhes adicionais"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
